import CommandHandler from 'diagram-js/lib/command/CommandHandler';
import {Base, Shape} from 'diagram-js/lib/model';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {exclusiveGatewayType, nodesDetailsExtensionType, taskType} from '@ispm-shared/modules/bpmn';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {EventType} from '@ispm/shared/bpmn-io/bpmn-js';
import EventBus from 'diagram-js/lib/core/EventBus';
import {BpmnExtensionHelper} from '@ispm/shared/bpmn-io/bpmn-extension-helper';
import {constants} from '@ispm-client/modules/bpmn-modeler/constants/constants';
import {IdGenerator} from '@ispm-client/modules/bpmn-modeler/bpmn/utils/id-generator';
import ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import {ConnectionUtils} from "@ispm-client/modules/bpmn-modeler/bpmn/utils/connection-utils";
import {FloatingNodesService} from "@ispm/shared/bpmn-io/services/floating-nodes-service";
import {TreeNavigator} from "@ispm/shared/bpmn-io/services/tree-navigator";

export class AppendNodeHandler implements CommandHandler {

  private connectionUtils: ConnectionUtils;
  private verticalLayoutMode: boolean = false;

  constructor(private elementFactory, private autoPlace, private moddle, private modeling, private eventBus: EventBus,
              private bpmnExtensionHelper: BpmnExtensionHelper, private idGenerator: IdGenerator,
              private elementRegistry: ElementRegistry,
              private floatingNodesService: FloatingNodesService, private treeNavigator: TreeNavigator) {
    this.connectionUtils = new ConnectionUtils(this.elementRegistry);
    this.verticalLayoutMode = this.treeNavigator.getVerticalLayoutMode();
  }

  postExecute(context): any {
    const source = context.source;
    const nodeType = context.nodeType;
    const attrs = context.attrs || {};
    const neighborhoodID = context.params ? context.params.neighborhoodID : undefined;
    let result;
    switch (nodeType) {
      case NodeType.normal:
        result = this.appendNewNode(source, nodeType, attrs);
        break;
      case NodeType.neighborhood_entry:
        result = this.appendNewNode(source, nodeType, Object.assign(attrs, constants.NEIGHBORHOOD_NODE_DIMENSIONS));
        break;
      case NodeType.treatment:
        result = this.appendNewTreatmentNode(source, attrs);
        break;
      case NodeType.neighborhood_head:
        result = this.appendNewNeighborhoodNodeHead(source, attrs);
        break;
      default:
        console.log('AppendNodeHandler - unsupported shape nodeType: ' + nodeType);
    }
    const connection = result.node.incoming[0];
    const node = result.node;
    this.updateNodeID(result.node);
    this.connectionUtils.updateSequenceFlowID(connection, result.source, result.node);

    // update neighborhoodID
    if (ModelingUtil.getNodeType(node) === NodeType.neighborhood_head) {
      node.businessObject.neighborhoodID = node.id;
    } else {
      connection.businessObject.neighborhoodID = neighborhoodID ? neighborhoodID : this.treeNavigator.currentLayer;
      node.businessObject.neighborhoodID = neighborhoodID ? neighborhoodID : this.treeNavigator.currentLayer;
    }

    // if parent is floated so the new node is floated too
    this.floatingNodesService.addFloatedElementWhenAddingNewNode(source, result.node, node.businessObject.neighborhoodID);

    Object.assign(context, result);

    this.eventBus.fire(EventType.NODE_ADDED, {node: result.node, nodeType: result.nodeType});
  }


  public appendNewNode(sourceElement: Shape, nodeType: NodeType, attrs) {

    const newShape = this.elementFactory.createShape(Object.assign({
      type: taskType,
      width: constants.NODE_DIMENSIONS.width,
      height: constants.NODE_DIMENSIONS.height,
      nodeType: nodeType
    }, attrs), {});

    const newGateway = this.getGateway(sourceElement);
    newGateway.businessObject.neighborhoodID = this.treeNavigator.currentLayer;
    const newNode = this.autoPlace.append(newGateway, newShape);
    this.appendNodeType(newNode, nodeType);
    return {node: newNode, nodeType, source: newGateway};
  }


  public appendNewNeighborhoodNodeHead(sourceElement: Shape, attrs) {
    const newShape = this.elementFactory.createShape(Object.assign({
      type: taskType,
      width: constants.NEIGHBORHOOD_NODE_DIMENSIONS.width,
      height: constants.NEIGHBORHOOD_NODE_DIMENSIONS.height,
      nodeType: NodeType.neighborhood_head
    }, attrs), {});
    const newNode = this.autoPlace.append(sourceElement, newShape);
    this.appendNodeType(newNode, NodeType.neighborhood_head);
    return {node: newNode, nodeType: NodeType.neighborhood_head, source: sourceElement};
  }


  public appendNewTreatmentNode(sourceElement: Shape, attrs) {
    const newShape = this.elementFactory.createShape(Object.assign({
      type: taskType,
      width: this.verticalLayoutMode ? constants.TREATMENT_NODE_VERTICAL_LAYOUT_DIMENSIONS.width : constants.TREATMENT_NODE_DIMENSIONS.width,
      height: this.verticalLayoutMode ? constants.TREATMENT_NODE_VERTICAL_LAYOUT_DIMENSIONS.height :constants.TREATMENT_NODE_DIMENSIONS.height,
      nodeType: NodeType.treatment
    }, attrs), {});
    let newNode = this.autoPlace.append(sourceElement, newShape);
    this.appendNodeType(newNode, NodeType.treatment);
    return {node: newNode, nodeType: NodeType.treatment, source: sourceElement};
  }

  private getGateway(sourceElement: Shape): Base {
    const parentGateway = ModelingUtil.getNodeOutgoingConnections(sourceElement).find(connection => connection.target.type === exclusiveGatewayType);
    if (parentGateway != undefined) {
      return parentGateway.target;
    }
    return this.appendShapeToGateway(sourceElement);
  }

  private appendShapeToGateway(sourceElement: Shape): Shape {
    const gatewayShape = this.elementFactory.createShape({type: exclusiveGatewayType}, {});
    gatewayShape.width = constants.EXCLUSIVE_GATEWAY_DIMENSIONS.width;
    gatewayShape.height = constants.EXCLUSIVE_GATEWAY_DIMENSIONS.height;
    const gateWay = this.autoPlace.append(sourceElement, gatewayShape);
    this.connectionUtils.updateSequenceFlowID(gateWay.incoming[0], sourceElement, gatewayShape);
    return gateWay;
  }

  private appendNodeType(node: Shape, nodeType: NodeType) {
    const nodeDetails = ModelingUtil.getNodeDetails(node) || this.moddle.create(nodesDetailsExtensionType);
    nodeDetails.nodeType = nodeType;
    this.bpmnExtensionHelper.addOrReplaceExtension(node, nodeDetails);
  }

  private updateNodeID(element: Shape) {
    const id = `Node_${this.idGenerator.next()}`;
    this.modeling.updateProperties(element, {id});
  }
}

